// src/pages/KnowledgeBase/DebtStudioGuide.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, Menu, Search, X } from 'lucide-react';
import localDoc from './DebtStudioGuideData.json';

const NAV_GROUPS = [
  { label: 'Getting started', sectionIds: ['intro', 'access'] },
  {
    label: 'Issues',
    sectionIds: [
      'debt_issues_overview', 'debt_issues_file_ribbon', 'debt_issues_main',
      'issue_info', 'dates_terms', 'costs_sources', 'refunding', 'scale_builder',
      'scale_builder_shift_controls', 'coupon_spread_analyzer', 'solve_structure',
      'solve_structure_grid', 'existing_debt_service', 'reports',
      'reports_sources_uses', 'reports_stats', 'reports_bond_summary',
      'reports_debt_service', 'reports_capi',
    ],
  },
  {
    label: 'Portfolios',
    sectionIds: [
      'debt_portfolios_overview', 'debt_portfolios_file_ribbon',
      'debt_portfolios_main', 'debt_portfolios_debt_service',
      'debt_portfolios_analysis',
    ],
  },
  { label: 'Reference', sectionIds: ['export', 'glossary'] },
];

export default function DebtStudioGuide() {
  const sections = localDoc;
  const [activeId, setActiveId] = useState(() => hashSectionId() || sections[0]?.id);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [query, setQuery] = useState('');
  const sectionElements = useRef(new Map());
  const sectionsById = useMemo(
    () => new Map(sections.map((section) => [section.id, section])),
    [sections],
  );

  const searchResults = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return [];
    return sections
      .filter((section) => sectionSearchText(section).includes(normalizedQuery))
      .slice(0, 8);
  }, [query, sections]);

  useEffect(() => {
    const requestedId = hashSectionId();
    if (!requestedId) return;
    if (!sectionsById.has(requestedId)) return;
    window.requestAnimationFrame(() => {
      document.getElementById(requestedId)?.scrollIntoView({ block: 'start' });
      setActiveId(requestedId);
    });
  }, [sectionsById]);

  useEffect(() => {
    const restoreHashLocation = () => {
      const requestedId = hashSectionId();
      if (!requestedId) return;
      const target = document.getElementById(requestedId);
      if (target) target.scrollIntoView({ block: 'start' });
      if (sectionsById.has(requestedId)) setActiveId(requestedId);
    };

    window.addEventListener('popstate', restoreHashLocation);
    return () => window.removeEventListener('popstate', restoreHashLocation);
  }, [sectionsById]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target?.id) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-15% 0px -70% 0px', threshold: 0 },
    );
    sectionElements.current.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  const navigateTo = (id) => {
    const element = sectionElements.current.get(id);
    if (!element) return;
    window.history.pushState(null, '', `#${id}`);
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveId(id);
    setMobileNavOpen(false);
  };

  return (
    <div className="p-4 sm:p-6">
      <header className="sticky top-[3.25rem] z-30 mx-auto mb-5 max-w-screen-2xl bg-surface py-3">
        <div className="rounded-2xl border border-app bg-app p-4 shadow-sm sm:px-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">Knowledge Base</p>
          <div className="mt-1 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-brand1">DebtStudio User Guide</h1>
              <p className="mt-1 max-w-3xl text-sm text-muted">
                Instructions and definitions for structuring, analyzing, and reporting debt in
                DebtStudio.
              </p>
            </div>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg border border-app bg-app px-3 py-2 text-sm text-app shadow-sm lg:hidden"
              aria-expanded={mobileNavOpen}
              aria-controls="debt-studio-guide-nav"
              onClick={() => setMobileNavOpen((open) => !open)}
            >
              {mobileNavOpen ? <X size={17} /> : <Menu size={17} />}
              Contents
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-screen-2xl grid-cols-1 items-start gap-6 lg:grid-cols-[18rem_minmax(0,1fr)]">
        <aside
          id="debt-studio-guide-nav"
          className={`${mobileNavOpen ? 'block' : 'hidden'} rounded-2xl border border-app bg-app p-4 shadow-sm lg:sticky lg:top-44 lg:block lg:max-h-[calc(100vh-12rem)] lg:overflow-y-auto`}
        >
          <GuideNavigation
            activeId={activeId}
            navigateTo={navigateTo}
            query={query}
            searchResults={searchResults}
            sectionsById={sectionsById}
            setQuery={setQuery}
          />
        </aside>

        <main className="min-w-0 space-y-5">
          {sections.map((section) => (
            <Section
              key={section.id}
              section={section}
              registerElement={(element) => {
                if (element) sectionElements.current.set(section.id, element);
                else sectionElements.current.delete(section.id);
              }}
            />
          ))}
        </main>
      </div>
    </div>
  );
}

function GuideNavigation({ activeId, navigateTo, query, searchResults, sectionsById, setQuery }) {
  return (
    <>
      <div className="relative">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
          size={16}
        />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search this guide"
          aria-label="Search the DebtStudio guide"
          className="w-full rounded-lg border border-app bg-app py-2 pl-9 pr-3 text-sm text-app outline-none focus:border-brand1 focus:ring-1 focus:ring-brand1"
        />
      </div>

      {query.trim() ? (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Search results</p>
          {searchResults.length ? (
            <ul className="space-y-1">
              {searchResults.map((section) => (
                <li key={section.id}>
                  <NavButton
                    active={activeId === section.id}
                    label={section.title}
                    onClick={() => navigateTo(section.id)}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <p className="rounded-lg bg-app2 px-3 py-2 text-sm text-muted">No topics found.</p>
          )}
        </div>
      ) : (
        <nav className="mt-4" aria-label="DebtStudio guide contents">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted">Contents</p>
          <div className="space-y-5">
            {NAV_GROUPS.map((group) => (
              <div key={group.label}>
                <h2 className="mb-1 px-2 text-sm font-semibold text-app">{group.label}</h2>
                <ul className="space-y-0.5">
                  {group.sectionIds.map((id) => {
                    const section = sectionsById.get(id);
                    if (!section) return null;
                    return (
                      <li key={id}>
                        <NavButton
                          active={activeId === id}
                          label={navigationLabel(section.title)}
                          onClick={() => navigateTo(id)}
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </nav>
      )}
    </>
  );
}

function NavButton({ active, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'location' : undefined}
      className={`flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors ${
        active ? 'bg-brand1/10 font-medium text-brand1' : 'text-muted hover:bg-app2 hover:text-app'
      }`}
    >
      <span>{label}</span>
      {active && <ChevronRight aria-hidden="true" className="shrink-0" size={15} />}
    </button>
  );
}

function Section({ registerElement, section }) {
  const { id, title, type, body, items, note } = section;

  return (
    <section
      id={id}
      ref={registerElement}
      className="scroll-mt-48 rounded-2xl border border-app bg-app p-5 text-app shadow-sm sm:p-6"
    >
      <h2 className="text-lg font-semibold text-app">{title}</h2>

      {type === 'text' && body && (
        <p className="mt-2 whitespace-pre-line text-sm leading-6 text-app" dangerouslySetInnerHTML={{ __html: md(body) }} />
      )}
      {type === 'bullets' && Array.isArray(items) && (
        <ul className="mt-2 list-disc space-y-1.5 pl-5 text-sm leading-6 text-app">
          {items.map((li, index) => (
            <li
              key={index}
              dangerouslySetInnerHTML={{ __html: md(li) }}
            />
          ))}
        </ul>
      )}
      {type === 'steps' && Array.isArray(items) && (
        <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-sm leading-6 text-app">
          {items.map((li, index) => <li key={index} dangerouslySetInnerHTML={{ __html: md(li) }} />)}
        </ol>
      )}
      {note && (
        <div className="mt-3 rounded-lg bg-app2 px-3 py-2 text-xs text-muted">
          <strong>Note:</strong>{' '}
          <span dangerouslySetInnerHTML={{ __html: md(note) }} />
        </div>
      )}
    </section>
  );
}

function hashSectionId() {
  if (typeof window === 'undefined') return '';
  return decodeURIComponent(window.location.hash.replace(/^#/, ''));
}

function navigationLabel(title = '') {
  const separatorIndex = title.indexOf(':');
  return separatorIndex >= 0 ? title.slice(separatorIndex + 1).trim() : title;
}

function sectionSearchText(section) {
  return [section.title, section.body, ...(section.items || []), section.note]
    .filter(Boolean)
    .join(' ')
    .replace(/\*\*|`/g, '')
    .toLowerCase();
}

function md(s = '') {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code>$1</code>');
}
